<template>
  <date-picker
    ref="datepickerRef"
    v-model="modelValue"
    auto-apply
    :disabled="disabled"
    :dark="useSystemStore().isDark"
    :enable-time-picker="enableTimePicker"
    :format="auto.format"
    :locale="formatLocale"
    :text-input="{ format: auto.format }"
    :min-date="minDate"
    :max-date="maxDate"
    :teleport="true"
    :config="{
      allowPreventDefault: true,
      tabOutClosesMenu: true,
      closeOnAutoApply: true,
      allowStopPropagation: true
    }"
    class="border-0 bg-transparent box-border outline-none"
  >
    <template #dp-input="{ value }">
      <UiInput
        :value="value"
        :mask="auto.mask"
        :size="size"
        :placeholder="placeholder || auto.format?.toUpperCase()"
        :disabled="disabled"
        class="w-full"
        @input="onInput"
      />
    </template>
  </date-picker>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { isMatch, getDaysInMonth } from 'date-fns'
import { useSystemStore } from '@/stores'

import UiInput from './input.vue'

const props = defineProps<{
  placeholder?: string
  disabled?: boolean
  error?: any
  mask?: string | string[]
  togglePassword?: boolean
  size?: 'small' | 'medium' | 'large'
  enableTimePicker?: boolean
  format?: string
  minDate?: Date | string
  maxDate?: Date | string
}>()

const formatLocale = ref('pt-BR')

// Gera automaticamente a máscara e o formato do input (se não for passado pelo usuário)
const auto = computed(() => {
  const tmp = (new Date(Date.UTC(1999, 11, 31, 0, 0, 0)))
  const base = tmp.toLocaleDateString(undefined, { timeZone: 'UTC' })

  return {
    mask: props.mask || base.replaceAll(/[0-9]/g, '#'),
    format: props.format || base.replace('1999', 'yyyy').replace('12', 'MM').replace('31', 'dd')
  }
})

const modelValue = defineModel<Date | string>()

const datepickerRef = ref()

const updateData = (val?: string) => {
  val = val.trim()

  let hour = 0
  let minute = 0

  if (isMatch(val, auto.value.format)) {
    const dayPos = auto.value.format.indexOf('dd')
    const monthPos = auto.value.format.indexOf('MM')
    const yearPos = auto.value.format.indexOf('yyyy')
    const hourPos = auto.value.format.indexOf('HH')
    const minutePos = auto.value.format.indexOf('mm')

    let day = (dayPos !== -1) ? Number(val.substr(dayPos, 2)) : 0
    let month = (monthPos !== -1) ? Number(val.substr(monthPos, 2)) : 0
    let year = (yearPos !== -1) ? Number(val.substr(yearPos, 4)) : 0

    if(props.enableTimePicker) {
      hour = (hourPos !== -1) ? Number(val.substr(hourPos, 2)) : 0
      minute = (minutePos !== -1) ? Number(val.substr(minutePos, 2)) : 0
    }

    if (`${year}`.length < 4) return

    // Normaliza o mês
    if (month > 12) month = 12
    else if (Number(month < 1)) month = 1

    // Normaliza o dia
    const lastDMonth = getDaysInMonth(new Date(year, month - 1))
    if (day > lastDMonth) day = lastDMonth
    else if (day < 1) day = 1

    if(props.enableTimePicker) {
      // Normaliza hora e minuto
      if (hour > 23) hour = 23
      else if (hour < 0) hour = 0
      if (minute > 59) minute = 59
      else if (minute < 0) minute = 0

      return modelValue.value = new Date(year, month - 1, day, hour, minute)
    }

    modelValue.value = new Date(year, month - 1, day)
  }
}

const onInput = ({target}) => {
  if (!target.value) return
  updateData(target.value)
}

onMounted(() => {
  if (navigator.language.substring(0, 2) === 'en') formatLocale.value = 'en-US'
  else if (navigator.language.substring(0, 2) === 'es') formatLocale.value = 'es-ES'
})
</script>

<style>
.dp__input_wrap {
  @apply !border-0;
} 
</style>